import { Hotel, HotelFormData } from '../types/hotel';
import { get, post, put, patch, del } from '../utils/httpClient';

class HotelService {
  // Récupérer tous les hôtels
  async getHotels(): Promise<Hotel[]> {
    try {
      const response = await get('/api/v1/admin/hotels');

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des hôtels');
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur getHotels:', error);
      throw error;
    }
  }

  // Récupérer un hôtel par ID
  async getHotelById(id: string): Promise<Hotel> {
    try {
      const response = await get(`/api/v1/admin/hotels/${id}`);

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération de l\'hôtel');
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur getHotelById:', error);
      throw error;
    }
  }

  // Créer un nouvel hôtel
  async createHotel(hotelData: HotelFormData): Promise<Hotel> {
    try {
      const response = await post('/api/v1/admin/hotels', hotelData);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la création de l\'hôtel');
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur createHotel:', error);
      throw error;
    }
  }

  // Mettre à jour un hôtel
  async updateHotel(id: string, hotelData: Partial<HotelFormData>): Promise<Hotel> {
    try {
      const response = await put(`/api/v1/admin/hotels/${id}`, hotelData);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la mise à jour de l\'hôtel');
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur updateHotel:', error);
      throw error;
    }
  }

  // Supprimer un hôtel
  async deleteHotel(id: string): Promise<void> {
    try {
      const response = await del(`/api/v1/admin/hotels/${id}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la suppression de l\'hôtel');
      }
    } catch (error) {
      console.error('Erreur deleteHotel:', error);
      throw error;
    }
  }

  // Suspendre/Activer un hôtel
  async toggleHotelStatus(id: string, status: 'active' | 'suspended'): Promise<Hotel> {
    try {
      const response = await patch(`/api/v1/admin/hotels/${id}/status`, { status });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors du changement de statut');
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur toggleHotelStatus:', error);
      throw error;
    }
  }

  // Récupérer les statistiques des hôtels
  async getHotelStats(): Promise<{
    total: number;
    active: number;
    suspended: number;
    pending: number;
    totalRooms: number;
    averageOccupancy: number;
  }> {
    try {
      const response = await get('/api/v1/admin/hotels/stats');

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des statistiques');
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur getHotelStats:', error);
      throw error;
    }
  }
}

const hotelServiceInstance = new HotelService();

export default hotelServiceInstance;

// Named exports for convenience
export const getHotels = () => hotelServiceInstance.getHotels();
export const getHotelById = (id: string) => hotelServiceInstance.getHotelById(id);
export const createHotel = (hotelData: HotelFormData) => hotelServiceInstance.createHotel(hotelData);
export const updateHotel = (id: string, hotelData: Partial<HotelFormData>) => hotelServiceInstance.updateHotel(id, hotelData);
export const deleteHotel = (id: string) => hotelServiceInstance.deleteHotel(id);
export const toggleHotelStatus = (id: string, status: 'active' | 'suspended') => hotelServiceInstance.toggleHotelStatus(id, status);
export const getHotelStats = () => hotelServiceInstance.getHotelStats();
